import { useCallback } from 'react';
import { UploadService } from '../../../../service/UploadService';
import { useNotificationStore } from '../../../../store/storeNotifications';
import { normalizeUrls, isValidMediaFile } from '../utils/mediaUtils';

interface UseMediaUploadProps {
  flowId: string | undefined;
  hasConnectedData: boolean;
  onUrlsChange: (urls: string[]) => void;
}

export const useMediaUpload = ({
  flowId,
  hasConnectedData,
  onUrlsChange,
}: UseMediaUploadProps) => {
  const setNotification = useNotificationStore(state => state.setNotification);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!flowId || files.length === 0) return [];

      try {
        setNotification({
          type: 'info',
          message: 'Uploading files...',
        });

        const uploadPromises = files.map(async file => {
          const formData = new FormData();
          formData.append('file', file);
          const response = await UploadService.uploadFile(formData, flowId);
          return response.data?.src || '';
        });

        const uploadedUrls = await Promise.all(uploadPromises);
        const validUrls = uploadedUrls.filter(Boolean);

        if (validUrls.length > 0) {
          setNotification({
            type: 'success',
            message: 'Files uploaded successfully',
          });
        }

        return validUrls;
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error.message || 'Error uploading files',
        });
        return [];
      }
    },
    [flowId, setNotification],
  );

  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || hasConnectedData) return;

      const validFiles = Array.from(files).filter(isValidMediaFile);
      if (validFiles.length === 0) return;

      const uploadedUrls = await uploadFiles(validFiles);
      if (uploadedUrls.length > 0) {
        onUrlsChange(uploadedUrls);
      }
    },
    [hasConnectedData, uploadFiles, onUrlsChange],
  );

  const handleDropFiles = useCallback(
    async (files: File[], textUrls: string[] = []) => {
      if (hasConnectedData) return;

      const allUrls = [...textUrls];

      if (files.length > 0) {
        const validFiles = files.filter(isValidMediaFile);
        if (validFiles.length > 0) {
          const uploadedUrls = await uploadFiles(validFiles);
          allUrls.push(...uploadedUrls);
        }
      }

      if (allUrls.length > 0) {
        onUrlsChange(allUrls);
      }
    },
    [hasConnectedData, uploadFiles, onUrlsChange],
  );

  return {
    handleFileUpload,
    handleDropFiles,
  };
};
